// Editar con Estudio desde el lienzo — ADR-2026-09-24-editar-con-estudio-en-la-barra-del-lienzo.
//
// Lo mismo que "Editar con estudio" del visor de Creaciones (`handleEditWithStudio` en
// GeneratedImagesHistory): crea un proyecto NUEVO en el Estudio de Video con este video
// ya en la timeline y navega. Mismo gating: el Estudio es de Ultra (dev testers pasan),
// es de escritorio, y la primera vez se explica con `StudioIntroModal`. No tiene hoja
// propia: el "panel" son esos avisos y un cartel mientras se copia el video.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { DesktopOnlyModal } from '../../../components/DesktopOnlyModal';
import { FeatureLockedModal } from '../../../components/FeatureLockedModal';
import { StudioIntroModal } from '../../../components/StudioIntroModal';
import { isSeen as isOnboardingSeen, loadOnboardingSeen, markSeen as markOnboardingSeen } from '../../../lib/onboardingSeen';
import { getModePlanLock, type ModePlanLock } from '../../../lib/planCapabilities';
import { supabase } from '../../../lib/supabase';
import { createStudioProjectFromVideo } from '../../../utils/videoProject';
import { estadoDesdeRedes, recordarProyectoDeRedes, sesionDeRedes } from '../../../lib/estudioDesdeRedes';
import type { PropsDeHerramienta } from './herramienta';

type Paso = { tipo: 'revisando' } | { tipo: 'bloqueado'; lock: ModePlanLock } | { tipo: 'solo-compu' } | { tipo: 'intro'; userId: string | null } | { tipo: 'creando' };

export const PanelEditarConEstudio: React.FC<PropsDeHerramienta> = ({ celda, onCerrar, onError }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [paso, setPaso] = useState<Paso>({ tipo: 'revisando' });
  const empezado = useRef(false);

  const crear = useCallback(
    async (userId: string | null) => {
      const fileName = celda.datos.fileName;
      if (!fileName) {
        onError('No pudimos abrir este video.');
        onCerrar();
        return;
      }
      setPaso({ tipo: 'creando' });
      try {
        const projectId = await createStudioProjectFromVideo({
          companyId: celda.companyId,
          userId,
          fileName,
          name: celda.titulo || undefined,
        });
        // Desde una sesión de Redes, el proyecto queda recordado para esa sesión y el
        // editor sabe volver a ella (ADR-2026-10-05-el-estudio-desde-redes-queda-a-mano).
        const sesion = sesionDeRedes(pathname);
        if (sesion) {
          recordarProyectoDeRedes(sesion, projectId);
          navigate(`/editor-video/${projectId}`, { state: estadoDesdeRedes(pathname) });
        } else {
          navigate(`/editor-video/${projectId}`);
        }
      } catch (err) {
        console.error('createStudioProjectFromVideo failed', err);
        onError('No se pudo crear el proyecto en el Estudio. Probá de nuevo en un momento.');
        onCerrar();
      }
    },
    [celda, navigate, onCerrar, onError, pathname],
  );

  // El gating, una vez. `dev_tester` y `plan` se leen de la MISMA fila y en la misma
  // consulta, no de `useUserGroup`: el hook vale `null` en el primer login sin cache, y
  // como este efecto corre una sola vez, "mientras carga no se bloquea" (lo que hacen
  // Modos y Creaciones con una card o un botón que se vuelven a evaluar) acá dejaba pasar
  // a un no-Ultra para siempre — y ni la creación del proyecto ni `/editor-video/:id`
  // vuelven a mirar el plan. Si la consulta falla o no trae plan, NO se sigue: con
  // `null`, `getModePlanLock` no bloquea nada, y acá eso sería abrir el Estudio sin
  // saber si el plan llega. Se avisa y se cierra; el usuario puede volver a tocarlo.
  useEffect(() => {
    if (empezado.current) return;
    empezado.current = true;
    void (async () => {
      const { data, error } = await supabase
        .from('company_profiles')
        .select('plan, dev_tester')
        .eq('id_company', celda.companyId)
        .maybeSingle();
      const fila = data as { plan?: string | null; dev_tester?: boolean | null } | null;
      if (error || !fila || (fila.dev_tester !== true && !fila.plan)) {
        onError('No pudimos revisar tu plan. Probá de nuevo en un momento.');
        onCerrar();
        return;
      }
      if (fila.dev_tester !== true) {
        const lock = getModePlanLock('editor-video', fila.plan);
        if (lock) {
          setPaso({ tipo: 'bloqueado', lock });
          return;
        }
      }
      if (window.matchMedia('(max-width: 767px)').matches) {
        setPaso({ tipo: 'solo-compu' });
        return;
      }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const uid = user?.id ?? null;
      if (uid) await loadOnboardingSeen(uid);
      if (!isOnboardingSeen('estudio_from_historial')) {
        setPaso({ tipo: 'intro', userId: uid });
        return;
      }
      void crear(uid);
    })();
  }, [celda.companyId, crear, onCerrar, onError]);

  return (
    <>
      <FeatureLockedModal
        open={paso.tipo === 'bloqueado'}
        onClose={onCerrar}
        title={paso.tipo === 'bloqueado' ? paso.lock.title : ''}
        message={paso.tipo === 'bloqueado' ? paso.lock.message : ''}
      />
      <DesktopOnlyModal open={paso.tipo === 'solo-compu'} onClose={onCerrar} />
      <StudioIntroModal
        open={paso.tipo === 'intro'}
        onClose={onCerrar}
        onConfirm={() => {
          if (paso.tipo !== 'intro') return;
          markOnboardingSeen('estudio_from_historial', paso.userId);
          void crear(paso.userId);
        }}
      />
      {paso.tipo === 'creando' &&
        createPortal(
          <div className="fixed bottom-6 left-1/2 z-[250] -translate-x-1/2 px-4" role="status">
            <span className="flex items-center gap-2 rounded-2xl bg-brand-black py-3 px-5 text-sm text-white shadow-xl">
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
              Abriendo el video en el Estudio…
            </span>
          </div>,
          document.body,
        )}
    </>
  );
};
